<template>
  <div
    class="min-h-screen h-full font-barlow"
    :class="$style['layout-color']"
  >
    <div
      v-if="loading"
      class="flex items-center min-h-screen h-full"
    >
      <XSpinner class="text-4xl" />
    </div>

    <div
      v-else
      class="mx-auto"
      :class="$style['layout-size']"
    >
      <div
        v-if="!event.isTimelinePublished"
        class="text-center text-black font-medium py-4"
      >
        {{ $t('event.timeline.unpublish') }}
      </div>

      <div class="mb-8">
        <div class="relative flex items-center justify-center">
          <img
            :class="$style['header-image']"
            :src="headerImage"
          >
        </div>
      </div>

      <div v-if="errorMessage">
        {{ errorMessage }}
      </div>

      <div
        v-else
        class="mx-auto max-w-md h-full px-4"
      >
        <XTimeline
          :timelineBody="timelineTemplate.privateBody"
          :timelineConfig="timelineConfig"
          :expired="event.isTimelineExpired"
          :actions="actions"
          :reactions="reactions"
          :eventFeedback="eventFeedback"
          :questions="questions"
          @fieldsUpdated="fetchTemplate"
          @reactionAdded="fetchActions"
        />
      </div>

      <div
        class="text-center"
        v-if="event.timeline.config.downloadCertificateCV19"
      >
        <a
          :href="downloadCV19Url"
          target="_blank"
          class="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base leading-6 font-medium rounded no-underline text-white bg-info-60 hover:bg-info-40 focus:outline-none focus:shadow-outline transition duration-150 ease-in-out">
          {{ downloadCV19ButtonText }}
        </a>
      </div>

      <div
        v-if="event.timeline.template.footer.enabled"
        class="mt-10 py-4"
      >
        <div class="inline-flex w-full items-center justify-center">
          <div
            v-for="(social, index) in timelineTemplate.footer.socials"
            :key="index"
            class="inline-block"
            :class="{ 'mr-6': index + 1 < timelineTemplate.footer.socials.length }"
          >
            <a
              :href="social.link"
              target="_blank"
              class="no-underline"
            >
              <img
                :src="social.image"
                class="w-auto h-auto"
                :class="$style['footer-image']"
                alt="Social icon"
              >
            </a>
          </div>
        </div>
      </div>

      <div
        v-if="timelineTemplate.terms.enabled"
        class="py-3 px-4"
      >
        <div
          class="py-2 px-3 quill-class"
          v-html="timelineTemplate.terms.content"
        />
      </div>
    </div>
  </div>
</template>

<script>
import gql from 'graphql-tag'
import { mapState } from 'vuex'
import _ from 'lodash'
import { setupEcho } from '@plat/util/broadcast'

import XTimeline from './Index'

export default {
  components: {
    XTimeline
  },

  data () {
    return {
      icon: require('@common/assets/icons/fa/solid/star.svg'),
      actions: [],
      reactions: [],
      eventFeedback: null,
      questions: null,
      event: {
        timeline: {},
        actions: []
      },
      errorMessage: '',
      timelineTemplate: {},
      isDesktop: true
    }
  },

  computed: {
    ...mapState({
      eventId: state => state.routeParameters.event.id,
      attendeeId: state => state.routeParameters.attendee.id
    }),

    loading () {
      return this.$apollo.queries.event.loading
    },

    timelineConfig () {
      return this.event.timeline.config || {}
    },

    headerImage () {
      let mobileHeader = this.timelineTemplate.headerImageMobile
        ? this.timelineTemplate.headerImageMobile
        : this.timelineTemplate.headerImageDesktop

      return this.isDesktop
        ? this.timelineTemplate.headerImageDesktop
        : mobileHeader
    },

    downloadCV19Url () {
      return '/' + this.eventId + '/certificate/' + this.attendeeId + '/download'
    },

    downloadCV19ButtonText () {
      return this.$t('common.cv19certificate')
    }
  },

  created () {
    this.$store.dispatch('saveIsPublicTimeline', false)
  },

  mounted () {
    if ( // Boston Scientific
      this.eventId === '1b3d2d60-c865-49d9-9be7-554496918b16' ||
      this.eventId === '6b52ac79-41f0-4394-91eb-7bbe2ecb8bb8'
    ) {
      const echo = setupEcho()

      echo.channel('event.timeline.' + this.eventId)
        .listen('QuestionActionStarted', event => {
          let questionIndex = this.questions.findIndex(question => question.ref === event.data.question_ref)

          this.questions[questionIndex].isActive = true
          this.questions[questionIndex].isFinished = false
        })
        .listen('QuestionActionStopped', event => {
          let questionIndex = this.questions.findIndex(question => question.ref === event.data.question_ref)

          this.questions[questionIndex].isFinished = true

          setTimeout(() => {
            this.questions[questionIndex].isActive = false
          }, 35000)
        })
    }

    this.onResize()
    window.addEventListener('resize', this.onResize)
  },

  destroyed () {
    window.removeEventListener('resize', this.onResize)
  },

  methods: {
    onResize: _.throttle(function () {
      this.isDesktop = window.innerWidth > 668
    }, 300),

    fetchTemplate () {
      this.$apollo
        .query({
          query: gql`query Event ($eventId: Uuid!, $attendeeId: Uuid!) {
            event (eventId: $eventId) {
              timeline (attendeeId: $attendeeId) {
                template {
                  privateBody {
                    timeline
                    noReactions
                    closed
                  }
                }
              }
              attendee (attendeeId: $attendeeId) {
                timelineReactions (eventId: $eventId) {
                  id
                  type
                  url
                  data
                  createdAt
                  showUrl(eventId: $eventId, attendeeId: $attendeeId)
                  downloadUrl(eventId: $eventId)
                  interactions(attendeeId: $attendeeId) {
                    id
                    channel
                    type
                    content
                  }
                }
              }
            }
          }`,
          fetchPolicy: 'network-only',
          variables: {
            eventId: this.eventId,
            attendeeId: this.attendeeId
          }
        })
        .then(result => {
          this.timelineTemplate = {
            ...this.timelineTemplate,
            ...result.data.event.timeline.template
          }
          this.reactions = result.data.event.attendee.timelineReactions || []
        })
    },

    fetchActions () {
      this.$apollo
        .query({
          query: gql`query Event ($eventId: Uuid!, $actionTypeFilter: [String]) {
            event (eventId: $eventId) {
              actions (actionTypeFilter: $actionTypeFilter) {
                nodes {
                  id
                  name
                  type {
                    ref
                  }
                  settings
                }
              }
            }
          }`,
          fetchPolicy: 'network-only',
          variables: {
            eventId: this.eventId,
            actionTypeFilter: [
              'raffle'
            ]
          }
        })
        .then(result => {
          this.actions = result.data.event.actions.nodes
            .map(action => ({
              ...action,
              type: _.camelCase(action.type.ref)
            }))
        })
    }
  },

  apollo: {
    event: {
      query: gql`query ($eventId: Uuid!, $attendeeId: Uuid!, $actionTypeFilter: [String]) {
        event (eventId: $eventId) {
          id
          name
          startsAt
          endsAt
          isTimelineExpired
          isTimelinePublished
          timeline (attendeeId: $attendeeId) {
            config {
              downloadCertificateCV19
              share {
                channels {
                  facebook
                  twitter
                  linkedIn
                  whatsapp
                  email
                  download
                }
                description
                hashtags
                message
              }
              expressions {
                enabled
              }
              eventFeedback {
                enabled
                rateTitle
                commentTitle
              }
              scratchAndWin {
                enabled
                title
                subtitle
                wonMessage
                lostMessage
              }
            }
            template {
              headerImageDesktop
              headerImageMobile
              privateBody {
                timeline
                noReactions
                closed
              }
              footer {
                enabled
                socials {
                  link
                  image
                }
              }
              terms {
                enabled
                content
              }
            }
          }
          questions {
            ref
            name
            type
            answers
            correctAnswer
            isActive
            hasAnswered (attendeeId: $attendeeId)
          }
          formFields {
            type
            required
            web
            config {
              id
              type
              label
              url
              options
              rules
            }
          }
          attendee (attendeeId: $attendeeId) {
            id
            firstName
            lastName
            secondLastName
            email
            phone
            gender
            birthYear
            postalCode
            ticketType
            birthDate
            title
            country
            interests
            extra
            status
            timelineReactions (eventId: $eventId) {
              id
              type
              url
              data
              createdAt
              showUrl(eventId: $eventId, attendeeId: $attendeeId)
              downloadUrl(eventId: $eventId)
              interactions(attendeeId: $attendeeId) {
                id
                channel
                type
                content
              }
            }
            eventFeedback {
              data
            }
          }
          actions (actionTypeFilter: $actionTypeFilter) {
            nodes {
              id
              name
              type {
                ref
              }
              settings
            }
          }
        }
      }`,
      variables () {
        return {
          eventId: this.eventId,
          attendeeId: this.attendeeId,
          actionTypeFilter: [
            'raffle'
          ]
        }
      },
      update (result) {
        return result.event
      },
      result (result) {
        const event = result.data.event

        this.$store.dispatch(
          'storeAttendee',
          _.pickBy(
            event.attendee,
            (value, key) => key !== 'timelineReactions' &&
                            key !== 'eventFeedback'
          )
        )

        this.$store.dispatch(
          'storeFormFields',
          event.formFields.filter(field => field.config && field.config.type !== 'html')
        )

        this.timelineTemplate = event.timeline.template
        this.reactions = event.attendee.timelineReactions || []
        this.eventFeedback = event.attendee.eventFeedback
        this.actions = event.actions.nodes.map(action => ({
          ...action,
          type: _.camelCase(action.type.ref)
        }))

        this.questions = event.questions
          .map(question => ({
            ...question,
            isFinished: false
          }))
      },
      error (error) {
        if (error.graphQLErrors && error.graphQLErrors.length !== 0) {
          this.errorMessage = error.graphQLErrors[0].message
        } else {
          this.errorMessage = this.$t('common.notifications.messages.requestError')
        }
      }
    }
  }
}
</script>

<style
  lang="scss"
  module
>
.header-image {
  display: block;
  max-width: 100%;
  max-height: 400px;
  margin: 0 auto;
}

.layout-size {
  max-width: 1800px;
}

.layout-color {
  background-color: #fafafa;
}

.terms {
  p {
    line-height: 1;
  }
}
</style>

<style lang="scss">
.quill-class {
  strong {
    font-weight: 700;
  }
}
</style>
